// Badge types and their details
export const BADGE_TYPES = {
  FOUNDING_MEMBER: {
    id: 'founding-member',
    name: 'Founding Member',
    // Public description (shown in tooltip)
    tooltipDescription: 'Founding Member',
    // Public description (shown in profile)
    publicDescription: 'One of the first 25 members of their chapter.',
    // Full description with discount info (for backend use only)
    description: 'One of the first 25 members of their chapter. Receives 50% discount on first year membership.',
    image: '/ap-badges/founding-member-badge-nobackground.png',
    discountPercentage: 50
  },
  VIP_MEMBER: {
    id: 'vip-member',
    name: 'VIP Member',
    tooltipDescription: 'VIP Member',
    publicDescription: 'One of the members #26-50 of their chapter.',
    description: 'One of the members #26-50 of their chapter. Receives 25% discount on first year membership.',
    image: '/ap-badges/vip-member-badge-nobackground.png',
    discountPercentage: 25
  },
  FIVE_YEAR_MEMBER: {
    id: '5-year-member',
    name: '5 Year Member',
    tooltipDescription: '5 Year Member',
    publicDescription: 'A member for 5 consecutive years.',
    description: 'A member for 5 consecutive years. Receives 25% off of 5 event registrations.',
    image: '/ap-badges/5-year-badge.png',
    eventDiscounts: 5,
    discountPercentage: 25
  },
  TEN_YEAR_MEMBER: {
    id: '10-year-member',
    name: '10 Year Member',
    tooltipDescription: '10 Year Member',
    publicDescription: 'A member for 10 consecutive years.',
    description: 'A member for 10 consecutive years. Receives 30% off of 10 event registrations.',
    image: '/ap-badges/10-year-member-badge-nobackground.png',
    eventDiscounts: 10,
    discountPercentage: 30
  },
  CHAPTER_PRESIDENT: {
    id: 'chapter-president',
    name: 'Chapter President',
    tooltipDescription: 'Chapter President',
    publicDescription: 'President of a local APN chapter.',
    description: 'President of a local APN chapter.',
    image: '/ap-badges/chapter-badge-president-nobackground.png'
  },
  AMBASSADOR: {
    id: 'ambassador',
    name: 'Ambassador',
    tooltipDescription: 'Ambassador',
    publicDescription: 'Member who has successfully referred multiple people to APN events or membership.',
    description: 'Member who has successfully referred multiple people to APN events or membership. Receives automatic discount on next membership or sponsorship package.',
    image: '/ap-badges/vip-member-badge-nobackground.png', // Temporary - using VIP badge until ambassador badge is added
    discountPercentage: 10
  },
  LINKEDIN_CONNECTED: {
    id: 'linkedin-connected',
    name: 'LinkedIn Connected',
    tooltipDescription: 'LinkedIn Connected',
    publicDescription: 'Linked their LinkedIn profile.',
    description: 'Linked their LinkedIn profile to their APN account for easier networking.',
    image: '/ap-badges/linkedin-badge.png',
    emoji: '🔗'
  },
  CONNECTOR: {
    id: 'connector',
    name: 'Connector',
    tooltipDescription: 'Connector — referred a new member',
    publicDescription: 'Referred someone to APN.',
    description: 'Referred at least one new person to APN. Refer 5+ to become an Ambassador.',
    image: '/ap-badges/connector-badge.png',
    emoji: '🤝'
  }
};

// Per-year artwork for the "Class of YYYY" badge — TRANSPARENT-background PNGs in
// public/ap-badges/ (navy backgrounds were flood-filled out so they flow on the
// white UI). Years without art fall back to the 🎓 emoji automatically.
const CLASS_BADGE_IMAGES = {
  2023: '/ap-badges/class-of-2023.png',
  2024: '/ap-badges/class-of-2024.png',
  2025: '/ap-badges/class-of-2025.png',
  2026: '/ap-badges/class-of-2026.png',
};

// Helper function to get chapter-specific badge image
export const getChapterBadgeImage = (badgeType, chapter) => {
  if (!chapter) return null;

  const lowerChapter = chapter.toLowerCase();

  // Map of supported chapters and their badge images
  const chapterBadges = {
    'new york': {
      'chapter-president': '/ap-badges/chapter-badge-president-ny-nobackground.png'
    },
    'chicago': {
      'chapter-president': '/ap-badges/chapter-badge-president-chicago-nobackground.png'
    },
    'los angeles': {
      'chapter-president': '/ap-badges/chapter-badge-president-la-nobackground.png'
    },
    'boston': {
      'chapter-president': '/ap-badges/chapter-badge-president-nobackground.png'
    }
  };

  // If we have a specific badge for this chapter, return it
  if (chapterBadges[lowerChapter] && chapterBadges[lowerChapter][badgeType]) {
    return chapterBadges[lowerChapter][badgeType];
  }

  // Otherwise return the default badge
  return BADGE_TYPES[badgeType]?.image;
};

// Calculate membership duration in years
export const calculateMembershipYears = (memberSinceDate) => {
  if (!memberSinceDate) return 0;

  // Normalize whatever we were given into a real Date. In production
  // memberSince is usually a Firestore Timestamp (from serverTimestamp()),
  // NOT the "May 2023" string this function originally assumed — calling
  // .split() on a Timestamp threw and broke the entire Member Directory.
  let startDate = null;

  if (typeof memberSinceDate.toDate === 'function') {
    startDate = memberSinceDate.toDate();                     // Firestore Timestamp
  } else if (memberSinceDate instanceof Date) {
    startDate = memberSinceDate;
  } else if (typeof memberSinceDate === 'object' && typeof memberSinceDate.seconds === 'number') {
    startDate = new Date(memberSinceDate.seconds * 1000);     // plain {seconds,nanoseconds}
  } else if (typeof memberSinceDate === 'string') {
    const parts = memberSinceDate.trim().split(' ');
    const monthMap = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3,
      'May': 4, 'June': 5, 'July': 6, 'August': 7,
      'September': 8, 'October': 9, 'November': 10, 'December': 11
    };
    if (parts.length === 2 && monthMap[parts[0]] !== undefined && !isNaN(parseInt(parts[1]))) {
      startDate = new Date(parseInt(parts[1]), monthMap[parts[0]], 1); // legacy "May 2023"
    } else {
      const parsed = new Date(memberSinceDate);                // ISO string / other
      startDate = isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  if (!startDate || isNaN(startDate.getTime())) return 0;

  const now = new Date();
  let years = now.getFullYear() - startDate.getFullYear();
  // Adjust if this year's anniversary hasn't happened yet.
  if (now.getMonth() < startDate.getMonth() ||
      (now.getMonth() === startDate.getMonth() && now.getDate() < startDate.getDate())) {
    years--;
  }

  return Math.max(0, years);
};

// Normalize memberSince (Timestamp / Date / "May 2023" / ISO) into a join year.
const getJoinYear = (memberSince) => {
  if (!memberSince) return null;
  let d = null;
  if (typeof memberSince.toDate === 'function') d = memberSince.toDate();
  else if (memberSince instanceof Date) d = memberSince;
  else if (typeof memberSince === 'object' && typeof memberSince.seconds === 'number') d = new Date(memberSince.seconds * 1000);
  else if (typeof memberSince === 'string') {
    const parts = memberSince.trim().split(' ');
    if (parts.length === 2 && !isNaN(parseInt(parts[1]))) return parseInt(parts[1]); // "May 2023"
    const parsed = new Date(memberSince);
    if (!isNaN(parsed.getTime())) d = parsed;
  }
  return d && !isNaN(d.getTime()) ? d.getFullYear() : null;
};

// Determine which badges a user has earned
export const determineUserBadges = (userData) => {
  if (!userData) return [];

  const badges = [];

  // Check for founding member badge
  if (userData.isFoundingMember || userData.email === 'admin@the-apn.com') {
    badges.push({
      ...BADGE_TYPES.FOUNDING_MEMBER,
      earnedDate: userData.memberSince
    });
  }

  // Check for VIP member badge
  if (userData.isVipMember || userData.email === 'admin@the-apn.com') {
    badges.push({
      ...BADGE_TYPES.VIP_MEMBER,
      earnedDate: userData.memberSince
    });
  }

  // Check for 5-year and 10-year membership badges
  const membershipYears = calculateMembershipYears(userData.memberSince);

  if (membershipYears >= 10) {
    badges.push({
      ...BADGE_TYPES.TEN_YEAR_MEMBER,
      earnedDate: `${new Date().toLocaleString('default', { month: 'long' })} ${new Date().getFullYear()}`
    });
  } else if (membershipYears >= 5) {
    badges.push({
      ...BADGE_TYPES.FIVE_YEAR_MEMBER,
      earnedDate: `${new Date().toLocaleString('default', { month: 'long' })} ${new Date().getFullYear()}`
    });
  }

  // Check for chapter president badge
  if (userData.role === 'Chapter President') {
    const badge = {
      ...BADGE_TYPES.CHAPTER_PRESIDENT,
      name: `${userData.chapter} Chapter President`,
      tooltipDescription: `${userData.chapter} Chapter President`,
      earnedDate: userData.roleStartDate || userData.memberSince
    };

    // Get chapter-specific badge image if available
    const chapterBadgeImage = getChapterBadgeImage('chapter-president', userData.chapter);
    if (chapterBadgeImage) {
      badge.image = chapterBadgeImage;
    }

    badges.push(badge);
  }

  // Check for Ambassador badge
  if (userData.referralCount && userData.referralCount >= 5) {
    badges.push({
      ...BADGE_TYPES.AMBASSADOR,
      earnedDate: userData.ambassadorSince || new Date().toLocaleString('default', { month: 'long', year: 'numeric' })
    });
  }

  // LinkedIn connected badge — earned by linking a LinkedIn profile.
  if (typeof userData.linkedinUrl === 'string' && /linkedin\.com/i.test(userData.linkedinUrl)) {
    badges.push({ ...BADGE_TYPES.LINKEDIN_CONNECTED });
  }

  // Connector badge — earned by referring 1-4 people (5+ becomes Ambassador).
  if (userData.referralCount && userData.referralCount >= 1 && userData.referralCount < 5) {
    badges.push({ ...BADGE_TYPES.CONNECTOR });
  }

  // "Class of YYYY" badge — the year the member joined APN.
  const joinYear = getJoinYear(userData.memberSince);
  if (joinYear) {
    badges.push({
      id: `class-of-${joinYear}`,
      name: `Class of ${joinYear}`,
      tooltipDescription: `Class of ${joinYear} — joined APN in ${joinYear}`,
      publicDescription: `Joined APN in ${joinYear}.`,
      image: CLASS_BADGE_IMAGES[joinYear],
      emoji: '🎓'
    });
  }

  return badges;
};
